// apps/client/src/game/testFinders/villagers.ts (M2-T11) — `villager`: the visible villager nearest `near` (playback poses);
// `villagerDoor`: the nearest villager house door outdoors (tile centre); `freePlot` (v1 round 4): the door of the nearest plot nobody
// lives on (no house there with an occupant), null once every plot is taken. Used by M2-T12's e2e.
import type { HouseState } from '@whiskerton/shared';
import { useGame } from '../../state/store';
import { playbackOf } from '../systems/villagers';
import type { Game } from '../game';
import type { TestFinder } from '../testHooks';

const d2 = (a: { x: number; y: number }, b: { x: number; y: number } | null) => (b ? (a.x - b.x) ** 2 + (a.y - b.y) ** 2 : 0);
function nearest<T extends { x: number; y: number }>(items: T[], near: { x: number; y: number } | null): T | null {
  return items.reduce<T | null>((best, it) => (!best || d2(it, near) < d2(best, near) ? it : best), null);
}
export const finders: readonly TestFinder[] = [
  { kind: 'villager', find: (game: Game, near) => {
    const pb = playbackOf(game);
    const out = useGame.getState().villagersList.flatMap((v) => { const p = pb?.poseOf(v.id); return p?.visible ? [{ id: v.id, x: p.pos.x, y: p.pos.y }] : []; });
    return nearest(out, near);
  } },
  { kind: 'villagerDoor', find: (_game, near) => {
    const s = useGame.getState();
    const out = s.villagersList.flatMap((v) => { const door = s.map?.plots[v.plot]?.door; return door ? [{ id: v.id, x: door.x + 0.5, y: door.y + 0.5 }] : []; });
    return nearest(out, near);
  } },
  { kind: 'freePlot', find: (game: Game, near) => {
    const taken = new Set(game.objects('house').flatMap((o) => { const h = o.state as HouseState; return h.occupant ? [h.plot] : []; }));
    const out = (useGame.getState().map?.plots ?? []).flatMap((p, i) => (taken.has(i) ? [] : [{ id: `plot:${i}`, x: p.door.x + 0.5, y: p.door.y + 0.5 }]));
    return nearest(out, near);
  } },
];
